import { useState } from "react";
import { Link, useParams } from "react-router-dom";
import MediaCard from "@/components/feature/MediaCard";
import { useFavorites } from "@/hooks/useFavorites";
import { useSEO } from "@/hooks/useSEO";
import { getMediaById, getRelated, formatDate } from "@/utils/media";

export default function Detail() {
  const { id } = useParams<{ id: string }>();
  const item = getMediaById(id);
  const { isFavorite, toggleFavorite } = useFavorites();
  const [toast, setToast] = useState("");

  useSEO({
    title: item ? `${item.title}（${item.year}） - 西瓜影院` : "影片详情 - 西瓜影院",
    description: item
      ? `${item.title}（${item.year}）是一部${item.genres.join("、")}类${item.type === "movie" ? "电影" : "电视剧"}，由${item.director}执导，${item.cast.join("、")}主演。${item.synopsis.slice(0, 60)}`
      : "西瓜影院影片详情页，展示影片基本信息、剧情简介与相关推荐。",
    keywords: item ? `${item.title},${item.genres.join(",")},${item.region},西瓜影院` : "影片详情,西瓜影院",
  });

  if (!item) {
    return (
      <div className="mx-auto flex flex-col items-center justify-center px-4 py-24 text-center md:px-8">
        <span className="flex h-16 w-16 items-center justify-center rounded-full bg-background-200 text-foreground-500">
          <i className="ri-film-line text-3xl" aria-hidden="true"></i>
        </span>
        <h1 className="mt-4 font-heading text-2xl font-700 text-foreground-950">影片不存在或已下线</h1>
        <p className="mt-2 text-sm text-foreground-500">你访问的影片可能已被移除，去看看其他精彩内容吧。</p>
        <Link
          to="/"
          className="mt-6 inline-flex h-10 items-center gap-2 rounded-md bg-primary-500 px-5 text-sm font-500 text-background-950 transition-colors hover:bg-primary-600"
        >
          <i className="ri-home-4-line text-base" aria-hidden="true"></i>
          返回首页
        </Link>
      </div>
    );
  }

  const related = getRelated(item, 6);
  const favorited = isFavorite(item.id);
  const typeLabel = item.type === "movie" ? "电影" : "电视剧";

  const showToast = (message: string) => {
    setToast(message);
    window.setTimeout(() => setToast(""), 2200);
  };

  const handleFavorite = () => {
    const added = toggleFavorite(item.id);
    showToast(added ? "已加入我的收藏" : "已取消收藏");
  };

  const handleShare = async () => {
    const basePath = __BASE_PATH__.replace(/\/$/, "");
    const url = `${window.location.origin}${basePath}/detail/${item.id}`;
    try {
      await navigator.clipboard.writeText(url);
      showToast("链接已复制到剪贴板");
    } catch {
      showToast("复制失败，请手动复制地址栏链接");
    }
  };

  return (
    <div className="pb-6">
      {/* Hero */}
      <section className="relative h-[300px] w-full overflow-hidden sm:h-[360px] md:h-[420px]">
        <img
          src={item.backdrop}
          alt={`${item.title} 剧照背景`}
          title={`${item.title} 影片详情`}
          className="h-full w-full object-cover object-top"
        />
        <div className="absolute inset-0 bg-gradient-to-r from-background-50 via-background-50/80 to-background-50/40" />
        <div className="absolute inset-0 bg-gradient-to-t from-background-50 via-background-50/40 to-transparent" />
      </section>

      <div className="mx-auto -mt-32 px-4 md:-mt-40 md:px-8">
        <nav aria-label="面包屑" className="mb-5 flex flex-wrap items-center gap-2 text-sm text-foreground-400">
          <Link to="/" className="transition-colors hover:text-primary-400">
            首页
          </Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          <Link
            to={item.type === "movie" ? "/category/movie" : "/category/tv"}
            className="transition-colors hover:text-primary-400"
          >
            {typeLabel}
          </Link>
          <i className="ri-arrow-right-s-line" aria-hidden="true"></i>
          <span className="truncate text-foreground-200">{item.title}</span>
        </nav>

        <div className="flex flex-col gap-6 md:flex-row md:items-start md:gap-8">
          <div className="relative w-40 shrink-0 overflow-hidden rounded-lg border border-background-300 sm:w-48 md:w-56" style={{ aspectRatio: "2 / 3" }}>
            <img
              src={item.cover}
              alt={`${item.title}（${item.year}）海报`}
              title={`${item.title} 高清海报`}
              className="h-full w-full object-cover object-top"
            />
          </div>

          <div className="flex-1">
            <div className="flex flex-wrap items-center gap-2">
              <span className="rounded-md bg-primary-500 px-2 py-0.5 text-xs font-500 text-background-950">{typeLabel}</span>
              <span className="rounded-md bg-accent-500/15 px-2 py-0.5 text-xs font-500 text-accent-400">{item.quality}</span>
              <span className="flex items-center gap-1 text-sm font-700 text-accent-400">
                <i className="ri-star-fill text-sm" aria-hidden="true"></i>
                {item.rating.toFixed(1)}
              </span>
            </div>

            <h1 className="mt-3 font-heading text-2xl font-700 leading-tight text-foreground-950 md:text-4xl">
              {item.title}（{item.year}）
            </h1>

            <div className="mt-3 flex flex-wrap items-center gap-x-3 gap-y-1 text-sm text-foreground-400">
              <span>{item.region}</span>
              <span>·</span>
              <span>{item.genres.join(" / ")}</span>
              <span>·</span>
              <span>{item.type === "movie" ? item.duration : `全 ${item.seasons} 季 · ${item.episodes} 集`}</span>
              <span>·</span>
              <span>更新于 {formatDate(item.updatedAt)}</span>
            </div>

            <div className="mt-5 flex flex-wrap gap-3">
              <button
                type="button"
                onClick={handleFavorite}
                className={`inline-flex h-11 items-center gap-2 rounded-md px-5 text-sm font-500 transition-colors ${
                  favorited
                    ? "bg-accent-500 text-background-950 hover:bg-accent-600"
                    : "border border-background-300 text-foreground-300 hover:border-accent-500 hover:text-accent-400"
                }`}
              >
                <i className={favorited ? "ri-heart-3-fill text-base" : "ri-heart-3-line text-base"} aria-hidden="true"></i>
                {favorited ? "已收藏" : "收藏"}
              </button>
              <button
                type="button"
                onClick={handleShare}
                className="inline-flex h-11 items-center gap-2 rounded-md border border-background-300 px-5 text-sm font-500 text-foreground-300 transition-colors hover:border-primary-500 hover:text-primary-400"
              >
                <i className="ri-share-forward-line text-base" aria-hidden="true"></i>
                分享链接
              </button>
              <button
                type="button"
                disabled
                title="演示站点，暂不提供实际播放"
                className="inline-flex h-11 cursor-not-allowed items-center gap-2 rounded-md bg-primary-500/60 px-5 text-sm font-500 text-background-950"
              >
                <i className="ri-play-circle-line text-lg" aria-hidden="true"></i>
                立即播放（演示）
              </button>
            </div>

            <dl className="mt-6 grid grid-cols-1 gap-x-8 gap-y-3 text-sm sm:grid-cols-2">
              <div className="flex gap-2">
                <dt className="shrink-0 text-foreground-500">导演：</dt>
                <dd className="text-foreground-300">{item.director}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="shrink-0 text-foreground-500">主演：</dt>
                <dd className="text-foreground-300">{item.cast.join("、")}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="shrink-0 text-foreground-500">类型：</dt>
                <dd className="text-foreground-300">{item.genres.join("、")}</dd>
              </div>
              <div className="flex gap-2">
                <dt className="shrink-0 text-foreground-500">{item.type === "movie" ? "片长：" : "集数："}</dt>
                <dd className="text-foreground-300">
                  {item.type === "movie" ? item.duration : `全 ${item.seasons} 季 · 共 ${item.episodes} 集`}
                </dd>
              </div>
            </dl>
          </div>
        </div>

        {/* Synopsis */}
        <section className="mt-10">
          <h2 className="mb-3 flex items-center gap-2 font-heading text-xl font-700 text-foreground-950">
            <span className="flex h-5 w-1 rounded-full bg-primary-500" />
            剧情简介
          </h2>
          <p className="max-w-4xl text-sm leading-7 text-foreground-400 md:text-base">{item.synopsis}</p>
        </section>

        {/* Related */}
        <section className="mt-12">
          <h2 className="mb-5 flex items-center gap-2 font-heading text-xl font-700 text-foreground-950 md:text-2xl">
            <span className="flex h-5 w-1 rounded-full bg-accent-500" />
            相关推荐
          </h2>
          <div className="grid grid-cols-2 gap-3 sm:grid-cols-3 md:gap-5 lg:grid-cols-6">
            {related.map((r) => (
              <MediaCard key={r.id} item={r} />
            ))}
          </div>
        </section>
      </div>

      {toast && (
        <div className="fixed bottom-6 left-1/2 z-50 -translate-x-1/2 rounded-md bg-foreground-950 px-5 py-3 text-sm font-500 text-background-50 shadow-lg">
          {toast}
        </div>
      )}
    </div>
  );
}